/**
 * Canonical member permission id strings stored on UserCompany.permissions
 * and used for route/UI gating in abrafi.
 */
export const permissionIds = {
  // Journals
  recordIncome: 'record_income',
  recordExpenses: 'record_expenses',
  billerManagement: 'biller_management',
  arInvoices: 'ar_invoices',
  equipmentManagement: 'equipment_management',
  capitalManagement: 'capital_management',
  recordCustomTransactions: 'record_custom_transactions',
  categories: 'categories',
  customers: 'customers',
  sendCustomerEmails: 'send_customer_emails',
  billInvoiceSettings: 'bill_invoice_settings',
  suppliers: 'suppliers',
  uploadEntry: 'upload_entry',
  uploadEntryUploadOnly: 'upload_entry_upload_only',
  taxCalendar: 'tax_calendar',
  taxCalendarViewOnly: 'tax_calendar_view_only',
  withholdingTaxReports: 'withholding_tax_reports',
  businessCalendar: 'business_calendar',
  eventsManagement: 'events_management',
  shop: 'shop',
  reserve: 'reserve',
  instoreqr: 'instoreqr',
  fileManager: 'file_manager',

  // Dashboard widgets
  dashboardTaxReminders: 'dashboard_tax_reminders',
  dashboardTotalIncome: 'dashboard_total_income',
  dashboardTotalExpenses: 'dashboard_total_expenses',
  dashboardTotalProfit: 'dashboard_total_profit',
  dashboardRevenueExpensesChart: 'dashboard_revenue_expenses_chart',
  dashboardPnlSummary: 'dashboard_pnl_summary',
  dashboardRecentTransactions: 'dashboard_recent_transactions',
  dashboardExpenseBreakdown: 'dashboard_expense_breakdown',

  // Reports — financial statements
  reportsIncomeStatement: 'reports_income_statement',
  reportsTrialBalance: 'reports_trial_balance',
  reportsBalanceSheet: 'reports_balance_sheet',
  reportsCashFlow: 'reports_cash_flow',
  reportsEquity: 'reports_equity',
  reportsFs: 'reports_fs',

  // Reports — journals
  reportsCashReceiptsJournal: 'reports_cash_receipts_journal',
  reportsCashDisbursementJournal: 'reports_cash_disbursement_journal',
  reportsGeneralJournal: 'reports_general_journal',
  reportsLedger: 'reports_ledger',
  posSystem: 'pos_system',

  // Settings — company settings
  settingsPeriodLocks: 'settings_period_locks',
  settingsCurrency: 'settings_currency',
  settingsCategoryAccountNumbers: 'settings_category_account_numbers',

  // Settings — company management
  viewCompanies: 'view_companies',
  editCompany: 'edit_company',
  apiKeys: 'api_keys',
  activityLogs: 'activity_logs',
  appsStore: 'apps_store',

  // HRIS (People) — admin domain
  hris: 'hris',
  hrisEmployees: 'hris_employees',
  hrisAttendance: 'hris_attendance',
  hrisLeave: 'hris_leave',
  hrisPayroll: 'hris_payroll',
  hrisSettings: 'hris_settings',
  hrisReports: 'hris_reports',

  // Employee Portal domain (not implied by admin `hris`)
  employeePortal: 'employee_portal',
  employeePortalAttendance: 'employee_portal_attendance',

  // Legacy (not shown in modal; mapped or stripped on save)
  hrisSelfService: 'hris_self_service',
  legacyCompanySettings: 'settings_company_settings',
  legacySettingsUsers: 'settings_users',
  legacyJournals: 'journals',
  legacyTransactionsAdd: 'transactions_add',
  legacyTransactionsView: 'transactions_view',
  legacyTransactionsEdit: 'transactions_edit',
  legacyTransactionsReceipt: 'transactions_receipt',
  legacyCashReceiptsJournal: 'cash_receipts_journal',
} as const;

export type PermissionId = (typeof permissionIds)[keyof typeof permissionIds];

/** Assignable journal permissions shown in Manage User Permissions. */
export const JOURNALS_MODAL_PERMISSION_IDS = [
  permissionIds.recordIncome,
  permissionIds.recordExpenses,
  permissionIds.billerManagement,
  permissionIds.arInvoices,
  permissionIds.equipmentManagement,
  permissionIds.capitalManagement,
  permissionIds.recordCustomTransactions,
  permissionIds.categories,
  permissionIds.customers,
  permissionIds.sendCustomerEmails,
  permissionIds.billInvoiceSettings,
  permissionIds.suppliers,
] as const;

export type JournalsModalPermissionId =
  (typeof JOURNALS_MODAL_PERMISSION_IDS)[number];

/** Assignable company-settings permissions shown in Manage User Permissions. */
export const COMPANY_SETTINGS_PERMISSION_IDS = [
  permissionIds.settingsPeriodLocks,
  permissionIds.settingsCurrency,
  permissionIds.settingsCategoryAccountNumbers,
] as const;

export type CompanySettingsPermissionId =
  (typeof COMPANY_SETTINGS_PERMISSION_IDS)[number];

export const HRIS_MODAL_PERMISSION_IDS = [
  permissionIds.hris,
  permissionIds.hrisEmployees,
  permissionIds.hrisAttendance,
  permissionIds.hrisLeave,
  permissionIds.hrisPayroll,
  permissionIds.hrisSettings,
  permissionIds.hrisReports,
] as const;

/** Assignable Employee Portal permissions shown in Manage User Permissions. */
export const EMPLOYEE_PORTAL_MODAL_PERMISSION_IDS = [
  permissionIds.employeePortal,
  permissionIds.employeePortalAttendance,
] as const;

export type EmployeePortalModalPermissionId =
  (typeof EMPLOYEE_PORTAL_MODAL_PERMISSION_IDS)[number];

/** Legacy company settings permission replaced by granular company-settings options. */
export const LEGACY_COMPANY_SETTINGS_PERMISSION_ID =
  permissionIds.legacyCompanySettings;

/** Permission ids that must not be stored on members (owner-only features). */
export const OWNER_ONLY_PERMISSION_IDS = [
  permissionIds.legacySettingsUsers,
] as const;

/** Legacy journal permission ids replaced by the simplified modal options. */
export const LEGACY_JOURNAL_PERMISSION_IDS = [
  permissionIds.legacyJournals,
  permissionIds.legacyTransactionsAdd,
  permissionIds.legacyTransactionsView,
  permissionIds.legacyTransactionsEdit,
  permissionIds.legacyTransactionsReceipt,
  permissionIds.legacyCashReceiptsJournal,
] as const;

/** Legacy self-service id replaced by Employee Portal permissions. */
export const LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID =
  permissionIds.hrisSelfService;
